import { Account } from 'api/Account/types';
import usePaginationSearchParams from 'common/hooks/usePaginationSearchParams';
import { HeadCell } from 'common/types/MuiComponents';
import DataTable from 'common/components/DataTable/DataTable';
import LoadingContainer from 'common/components/LoadingContainer/LoadingContainer';
import SearchUserInput from 'common/components/SearchUserInput/SearchUserInput';
import { useAccounts } from 'components/AdminDashboard/components/AccountsPage/useAccounts';
import AccountModal from 'components/AdminDashboard/components/AccountsPage/components/AccountModal/AccountModal';
import DeleteAccountPopover from 'components/AdminDashboard/components/AccountsPage/components/DeleteAccountPopover/DeleteAccountPopover';

import React, { MouseEvent, useCallback, useMemo, useState } from 'react';
import { Box, Button, Grid, IconButton, Paper, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';

interface Popover {
  el: HTMLElement | null;
  accountId: string | null;
}

const AccountsPage: React.FC = () => {
  const { accounts, getAccounts, isLoading, error, pagination, setAccounts } = useAccounts();
  const { pageParam, rowsParam, usernameParam } = usePaginationSearchParams();
  const [ searchValue, setSearchValue ] = useState('');

  const [ accountModal, setAccountModal ] = useState<Account | null | undefined>();

  const [ deletePopover, setDeletePopover ] = useState<Popover>({ el: null, accountId: null });
  const showDeletePopover = (e: MouseEvent<HTMLElement>, accountId: string) => setDeletePopover({ el: e.currentTarget, accountId });
  const closeDeletePopover = () => setDeletePopover({ el: null, accountId: null });

  const fetchAccounts = useCallback((abortController: AbortController) => {
    getAccounts(pageParam, rowsParam, usernameParam, abortController);
  }, [ getAccounts, pageParam, rowsParam, usernameParam ]);

  const headCells = useMemo((): HeadCell[] => [
    {
      id: 'id',
      numeric: false,
      label: 'Id',
      button: (
        <SearchUserInput
          onChange={e => setSearchValue(e.target.value)}
          placeholder="Search accounts"
        />
      ),
    },
    {
      id: 'accountId',
      numeric: false,
      label: 'Account id',
    },
    {
      id: 'name',
      numeric: false,
      label: 'Name',
    },
    {
      id: 'change',
      numeric: false,
      label: '',
    },
  ], []);

  const renderRows = useCallback(() => accounts.map(row => (
    <TableRow hover tabIndex={-1} key={row.id}>

      <TableCell>{row.id}</TableCell>

      <TableCell>{row.accountId}</TableCell>

      <TableCell>{row.name}</TableCell>

      <TableCell>
        <Box>

          <Tooltip title="Edit">
            <IconButton color="primary" size="small" onClick={() => setAccountModal(row)}>
              <Edit fontSize="inherit" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton color="primary" size="small" onClick={e => showDeletePopover(e, row.id)}>
              <Delete fontSize="inherit" />
            </IconButton>
          </Tooltip>

        </Box>
      </TableCell>

    </TableRow>
  )), [ accounts ]);

  return (
    <LoadingContainer isLoading={isLoading}>

      <Box mb={2} display="flex" flexDirection="row-reverse">
        <Button
          sx={{
            backgroundColor: 'white',
            textTransform: 'none',
          }}
          variant="text"
          onClick={() => setAccountModal(null)}
        >
          Add Account
        </Button>
      </Box>

      {error && (
        <Grid item xs={12} padding={2}>
          <Typography color="error" variant="subtitle2" sx={{ whiteSpace: 'break-spaces' }}>
            {error}
          </Typography>
        </Grid>
      )}

      <Paper sx={{ maxWidth: '100%' }} data-testid="accounts-page">
        <DataTable
          pagination={pagination}
          headCells={headCells}
          renderRows={renderRows}
          updateDataCallback={fetchAccounts}
          searchValue={searchValue}
        />
      </Paper>

      {accountModal !== undefined && (
        <AccountModal
          onCancel={() => setAccountModal(undefined)}
          account={accountModal}
          setAccounts={setAccounts}
        />
      )}

      {deletePopover.el && deletePopover.accountId && (
        <DeleteAccountPopover
          anchorEl={deletePopover.el}
          accountId={deletePopover.accountId}
          onClose={closeDeletePopover}
          setAccounts={setAccounts}
        />
      )}

    </LoadingContainer>
  );
};

export default AccountsPage;
